import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { cur } from '@/lib/currency';

function fmt(n) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Animated progress bar
function ProgressBar({ pct, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(Math.min(100, pct)), delay + 50);
    return () => clearTimeout(t);
  }, [pct, delay]);
  return (
    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${width}%`, backgroundColor: color, transitionDelay: `${delay}ms` }}
      />
    </div>
  );
}

// Avatar with performance-tier color
function RepAvatar({ name, pct }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('');

  let bg, color;
  if (pct >= 100) { bg = '#E1F5EE'; color = '#0F6E56'; }
  else if (pct >= 75) { bg = '#EEEDFE'; color = '#3C3489'; }
  else if (pct >= 50) { bg = '#FBEAF0'; color = '#993556'; }
  else if (pct >= 25) { bg = '#E6F1FB'; color = '#0C447C'; }
  else { bg = '#FCEBEB'; color = '#791F1F'; }

  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm"
      style={{ backgroundColor: bg, color }}>
      {initials || '؟'}
    </div>
  );
}

// Rank badge
function RankBadge({ rank }) {
  const medals = {
    1: { label: '١', bg: '#FAEEDA', color: '#92610A', icon: '🥇' },
    2: { label: '٢', bg: '#F1EFE8', color: '#6B6B6B', icon: '🥈' },
    3: { label: '٣', bg: '#FAECE7', color: '#9C4A27', icon: '🥉' },
  };
  const m = medals[rank];
  if (m) {
    return (
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black"
        style={{ backgroundColor: m.bg, color: m.color }}>
        {rank <= 3 ? m.icon.slice(0, 2) : m.label}
      </div>
    );
  }
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold text-muted-foreground bg-muted">
      {rank}
    </div>
  );
}

// Bar color by achievement %
function barColor(pct) {
  if (pct >= 75) return '#1D9E75';
  if (pct >= 50) return '#BA7517';
  return '#E24B4A';
}

// KPI card
function KpiCard({ label, value, suffix, sub }) {
  return (
    <div className="flex-1 bg-card border border-border rounded-xl p-3 text-center min-w-0">
      <p className="text-xs text-muted-foreground mb-0.5 truncate">{label}</p>
      <p className="font-black text-base text-foreground leading-tight">{value}</p>
      {suffix && <p className="text-xs text-muted-foreground">{suffix}</p>}
      {sub && <p className="text-xs font-medium mt-0.5" style={{ color: '#1D9E75' }}>{sub}</p>}
    </div>
  );
}

// Single rep row
function RepRow({ rep, rank, maxValue, metric, index, onTap }) {
  const value = metric === 'sales' ? rep.sales : metric === 'orders' ? rep.orders : rep.sales;
  const pct = maxValue > 0 ? Math.round((value / maxValue) * 100) : 0;
  // Target-based achievement (default target 100k for sales, 50 for orders)
  const target = metric === 'sales' ? (rep.target || 100000) : (rep.orders_target || 50);
  const achievement = target > 0 ? Math.round((value / target) * 100) : pct;
  const trend = rep.trend || 0;
  const color = barColor(achievement);
  const isTop = rank === 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.07 }}
      onClick={() => onTap && onTap(rep)}
      className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/30 hover:border-primary/20 cursor-pointer transition-all active:scale-[0.99]"
    >
      {/* Rank */}
      <RankBadge rank={rank} />

      {/* Avatar */}
      <RepAvatar name={rep.name} pct={achievement} />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold text-foreground leading-tight ${isTop ? 'text-base' : ''}`}>{rep.name}</p>
        <ProgressBar pct={pct} color={color} delay={index * 70} />
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">{fmt(rep.orders)} طلب</span>
          {trend !== 0 && (
            <span className={`flex items-center gap-0.5 text-xs font-semibold ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {trend > 0
                ? <TrendingUp className="w-3 h-3" />
                : <TrendingDown className="w-3 h-3" />
              }
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          )}
          <span className="text-xs px-1.5 py-0.5 rounded-md font-semibold"
            style={{ backgroundColor: color + '20', color }}>
            {achievement}%
          </span>
        </div>
      </div>

      {/* Value */}
      <div className="text-left flex-shrink-0">
        <p className={`text-sm font-black whitespace-nowrap ${isTop ? 'text-base' : ''}`}
          style={{ color: isTop ? '#1D9E75' : undefined }}>
          {metric === 'orders' ? fmt(value) : fmt(value)}
        </p>
        {metric === 'sales' && <p className="text-xs text-muted-foreground">{cur('ج.م')}</p>}
      </div>
    </motion.div>
  );
}

// Loading skeleton
function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card animate-pulse">
      <div className="w-7 h-7 rounded-full bg-muted flex-shrink-0" />
      <div className="w-9 h-9 rounded-full bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-muted rounded w-32" />
        <div className="h-1.5 bg-muted rounded-full w-full" />
        <div className="h-3 bg-muted rounded w-20" />
      </div>
      <div className="w-16 h-4 bg-muted rounded flex-shrink-0" />
    </div>
  );
}

export default function RepsLeaderboard({ reps = [], loading = false, dateFrom, dateTo }) {
  const [metric, setMetric] = useState('sales');
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_SHOW = 6;

  // Sort descending by selected metric
  const sorted = [...reps].sort((a, b) => {
    if (metric === 'orders') return b.orders - a.orders;
    return b.sales - a.sales;
  });

  const displayed = showAll ? sorted : sorted.slice(0, DEFAULT_SHOW);
  const maxValue = sorted[0] ? (metric === 'orders' ? sorted[0].orders : sorted[0].sales) : 1;

  // KPI calculations
  const totalSales = reps.reduce((s, r) => s + (r.sales || 0), 0);
  const avgSales = reps.length > 0 ? Math.round(totalSales / reps.length) : 0;
  const totalTarget = reps.length * 100000; // default 100k each
  const achievement = totalTarget > 0 ? Math.round((totalSales / totalTarget) * 100) : 0;

  // Date label
  const now = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const monthLabel = now.toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' });

  const METRIC_TABS = [
    { key: 'sales', label: 'المبيعات' },
    { key: 'orders', label: 'الطلبات' },
  ];

  if (!loading && reps.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-2">
        <p className="text-4xl">👥</p>
        <p className="font-bold text-foreground">لا يوجد مناديب لعرض بياناتهم</p>
        <p className="text-sm text-muted-foreground">لا توجد طلبات مبيعات في الفترة المحددة</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-bold text-foreground text-base">أداء المناديب</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {monthLabel} • {reps.length} مندوب
            </p>
          </div>
          {/* Metric toggle */}
          <div className="flex bg-muted rounded-xl p-0.5 gap-0.5">
            {METRIC_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setMetric(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  metric === tab.key
                    ? 'bg-card shadow text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        {!loading && (
          <div className="flex gap-2 mt-3">
            <KpiCard
              label="الإجمالي"
              value={totalSales >= 1000000 ? (totalSales / 1000000).toFixed(1) + 'م' : totalSales >= 1000 ? (totalSales / 1000).toFixed(0) + 'k' : fmt(totalSales)}
              suffix={cur('ج.م')}
            />
            <KpiCard
              label="المتوسط"
              value={avgSales >= 1000 ? (avgSales / 1000).toFixed(0) + 'k' : fmt(avgSales)}
              suffix={cur('ج.م')}
            />
            <KpiCard
              label="الإنجاز"
              value={`${achievement}٪`}
              sub={achievement >= 100 ? '✓ تجاوز الهدف' : undefined}
            />
          </div>
        )}
      </div>

      {/* Leaderboard rows */}
      <div className="p-3 space-y-2">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
        ) : (
          <AnimatePresence>
            {displayed.map((rep, i) => (
              <RepRow
                key={rep.id || rep.name}
                rep={rep}
                rank={i + 1}
                maxValue={maxValue}
                metric={metric}
                index={i}
              />
            ))}
          </AnimatePresence>
        )}

        {/* Note if few reps */}
        {!loading && reps.length > 0 && reps.length <= 2 && (
          <p className="text-xs text-center text-muted-foreground py-2">
            أضف المزيد من المناديب لمقارنة الأداء
          </p>
        )}

        {/* Show all / collapse */}
        {!loading && sorted.length > DEFAULT_SHOW && (
          <button
            onClick={() => setShowAll(v => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-primary hover:bg-primary/5 transition-colors border border-primary/20 mt-1"
          >
            {showAll
              ? <><ChevronUp className="w-3.5 h-3.5" /> إخفاء</>
              : <><ChevronDown className="w-3.5 h-3.5" /> عرض الكل ({sorted.length} مناديب)</>
            }
          </button>
        )}
      </div>
    </div>
  );
}